import React from 'react';
import {
    Alert, Box, Typography, ToggleButton, ToggleButtonGroup, TextField, Grid, IconButton,
} from '@mui/material';
import { PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import { AERIAL_LIFT_ITEMS } from '../constants';
import SelectSingleEmployeeModal from '../../../shared/SelectSingleEmployeeModal';

const toggleSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        px: 2, py: 0.5, fontSize: '0.75rem', textTransform: 'none',
        border: '1px solid #bdbdbd', minHeight: 32,
        '&.Mui-selected': { fontWeight: 600, border: '1px solid', '&:hover': { opacity: 0.9 } },
        '&:not(.Mui-selected)': { color: '#666' },
    },
};

export default function AerialLiftForm({ results, onChange, isReadOnly }) {
    const [operatorModalOpen, setOperatorModalOpen] = React.useState(false);

    // Default operator = logged in user
    React.useEffect(() => {
        if (!results.operatorPrintName) {
            const name = localStorage.getItem('employeeFullName') || '';
            if (name) onChange({ ...results, operatorPrintName: name });
        }
    }, []);

    const handleItemChange = (index, value) => {
        if (value !== null) {
            const key = `item${index + 1}`;
            onChange({ ...results, [key]: value });
        }
    };

    // Split items into 2 columns
    const half = Math.ceil(AERIAL_LIFT_ITEMS.length / 2);
    const col1 = AERIAL_LIFT_ITEMS.slice(0, half);
    const col2 = AERIAL_LIFT_ITEMS.slice(half);

    const renderItem = (item, globalIdx) => {
        const key = `item${globalIdx + 1}`;
        const val = results[key] || '';
        return (
            <Box key={globalIdx} sx={{
                p: 0.65, border: '1px solid', borderColor: val === 'fail' ? '#ffcdd2' : '#e0e0e0',
                backgroundColor: val === 'fail' ? '#fff5f5' : '#fafafa',
                display: 'flex', alignItems: 'center', gap: 0.65, mb: 0.5, borderRadius: 0.5,
            }}>
                <Typography variant="body2" sx={{ fontSize: '0.725rem', lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                    <strong style={{ color: '#888' }}>{globalIdx + 1}.</strong> {item}
                </Typography>
                {!isReadOnly ? (
                    <ToggleButtonGroup value={val} exclusive onChange={(_, v) => handleItemChange(globalIdx, v)}
                        size="small" sx={toggleSx}>
                        <ToggleButton value="pass" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', borderColor: '#4caf50 !important', '&:hover': { backgroundColor: '#45a049' } } }}>P</ToggleButton>
                        <ToggleButton value="fail" sx={{ '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', borderColor: '#f44336 !important', '&:hover': { backgroundColor: '#e53935' } } }}>F</ToggleButton>
                        <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#9e9e9e', color: '#fff', borderColor: '#9e9e9e !important', '&:hover': { backgroundColor: '#888' } } }}>NA</ToggleButton>
                    </ToggleButtonGroup>
                ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                        color: val === 'pass' ? '#4caf50' : val === 'fail' ? '#f44336' : '#9e9e9e' }}>
                        {val ? val.toUpperCase() : '—'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100', mb: 1, fontSize: '0.9rem' }}>
                AERIAL LIFT PRE-USE INSPECTION CHECKLIST
            </Typography>

            <Grid container spacing={0.5}>
                <Grid item xs={6}>
                    {col1.map((item, idx) => renderItem(item, idx))}
                </Grid>
                <Grid item xs={6}>
                    {col2.map((item, idx) => renderItem(item, half + idx))}
                </Grid>
            </Grid>

            <Alert severity="error" sx={{ mt: 2, fontSize: '0.7rem', py: 0.5 }}>
                IF THE AERIAL LIFT FAILS ANY PART OF THIS INSPECTION, REMOVE THE KEY AND REPORT THE PROBLEM TO YOUR SUPERVISOR. DO NOT ATTEMPT TO MAKE REPAIRS UNLESS YOU ARE A TRAINED AND AUTHORIZED SERVICE PERSON. ALL SERVICE IS TO BE DOCUMENTED. IF ANYTHING HAS BEEN JERRY-RIGGED NOTIFY YOUR SUPERVISOR AT ONCE.
            </Alert>

            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mt: 2, mb: 1 }}>
                Unit Information
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>Unit Type</Typography>
                    <ToggleButtonGroup
                        value={results.unitType || ''}
                        exclusive
                        onChange={(_, v) => { if (v !== null) onChange({ ...results, unitType: v }); }}
                        size="small"
                        disabled={isReadOnly}
                        sx={{
                            flexWrap: 'wrap', gap: 0.5,
                            '& .MuiToggleButton-root': {
                                px: 2, py: 0.5, fontSize: '0.75rem', textTransform: 'none',
                                border: '1px solid #bdbdbd', minHeight: 32,
                                '&.Mui-selected': { fontWeight: 600, backgroundColor: '#e65100', color: '#fff', borderColor: '#e65100 !important', '&:hover': { backgroundColor: '#d84315' } },
                                '&:not(.Mui-selected)': { color: '#666' },
                            },
                        }}
                    >
                        <ToggleButton value="scissor_lift">Scissor Lift</ToggleButton>
                        <ToggleButton value="articulating_boom">Articulating Boom</ToggleButton>
                        <ToggleButton value="man_lift">Man Lift</ToggleButton>
                        <ToggleButton value="other">Other</ToggleButton>
                    </ToggleButtonGroup>
                    {results.unitType === 'other' && (
                        <TextField label="Specify" size="small" fullWidth disabled={isReadOnly} sx={{ mt: 1 }}
                            value={results.unitTypeOther || ''} onChange={e => onChange({ ...results, unitTypeOther: e.target.value })} />
                    )}
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Aerial or Scissor Lift ID#" size="small" fullWidth disabled={isReadOnly}
                        value={results.liftId || ''} onChange={e => onChange({ ...results, liftId: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField label="Operator Print Name *" size="small" fullWidth value={results.operatorPrintName || ''} disabled
                            InputLabelProps={{ shrink: true }} />
                        {!isReadOnly && <IconButton onClick={() => setOperatorModalOpen(true)} color="primary" size="small"><PersonSearchIcon /></IconButton>}
                    </Box>
                </Grid>
            </Grid>

            <SelectSingleEmployeeModal open={operatorModalOpen} onClose={() => setOperatorModalOpen(false)}
                onSelect={(emp) => { onChange({ ...results, operatorPrintName: emp.FullName || emp.fullName || '' }); setOperatorModalOpen(false); }} />
        </Box>
    );
}
